/**
 * EFFET : chaque image de la caméra, pour l'analyse (MediaPipe) et le miroir du solo.
 *
 * UNE SEULE chaîne, la plus ordinaire possible : l'élément vidéo est copié dans un canvas 2D
 * LOGICIEL (`willReadFrequently`), et MediaPipe comme le miroir reçoivent ces pixels. Aucun
 * consommateur WebGL ne lit jamais l'image caméra directement (vidéo, `VideoFrame`, canvas
 * accéléré : même chemin d'import GPU), qui plantait sur Adreno 830 — carrés gris, image figée,
 * puis plus aucune image analysée (décision 46).
 */
import { startFrameLoop, type FrameLoopOptions, type FrameLoopState } from './frameLoop';

/** Image à analyser / dessiner : canvas réutilisé, valable seulement pendant l'appel de `onFrame`. */
export type CameraImage = HTMLCanvasElement;

export interface CameraFrames {
  stop(): void;
  state(): FrameLoopState;
}

/** `onError` : exception levée par `onFrame` (la boucle continue). */
export type CameraFramesOptions = Pick<FrameLoopOptions, 'onError'>;

/**
 * Copie l'image courante de la vidéo dans un canvas 2D réutilisé, sur le PROCESSEUR
 * (`willReadFrequently` : Chrome renonce au canvas accéléré). `null` : vidéo pas prête.
 */
export function createFrameCopier(): (video: HTMLVideoElement) => HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
  return (video) => {
    const { videoWidth: w, videoHeight: h } = video;
    if (!ctx || !w || !h) return null;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
  };
}

/** Appelle `onFrame` pour chaque nouvelle image de la caméra. */
export function onCameraFrames(
  video: HTMLVideoElement,
  onFrame: (image: CameraImage, t: number) => void,
  options: CameraFramesOptions = {},
): CameraFrames {
  const copy = createFrameCopier();
  return startFrameLoop(
    video,
    (t) => {
      const image = copy(video);
      if (image) onFrame(image, t);
    },
    options,
  );
}

/** Pixels RGBA de l'image copiée, prêts à être transférés à un worker ; `null` : canvas vide. */
export function readPixels(image: HTMLCanvasElement): ImageData | null {
  const ctx = image.getContext('2d', { alpha: false, willReadFrequently: true });
  if (!ctx || !image.width || !image.height) return null;
  return ctx.getImageData(0, 0, image.width, image.height);
}

/** Canvas réutilisé qui redessine des pixels rendus par le détecteur (l'image analysée, pour le miroir). */
export function createFrameCanvas(): (
  pixels: ArrayBuffer,
  width: number,
  height: number,
) => HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
  return (pixels, width, height) => {
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    ctx?.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
    return canvas;
  };
}
